import { FileDown, FileText, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePlan } from "@/hooks/usePlan";
import { useProfile } from "@/hooks/useProfile";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { ReportFilters, FinancialSummary, ProjectReport, ClientReport, TimeReport } from "@/lib/reports-engine";
import { generatePremiumHTML, type PremiumReportData } from "@/lib/report-html-generator";

interface ExportButtonsProps {
  filters: ReportFilters;
  financial?: FinancialSummary | null;
  projects?: ProjectReport[] | null;
  clients?: ClientReport[] | null;
  timeReport?: TimeReport | null;
  previousPeriod?: {
    revenue: number;
    hourlyRate: number;
  };
}

export function ExportButtons({
  filters,
  financial,
  projects,
  clients,
  timeReport,
  previousPeriod,
}: ExportButtonsProps) {
  const navigate = useNavigate();
  const { canExport } = usePlan();
  const { data: profile } = useProfile();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [isExportingHTML, setIsExportingHTML] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const buildReportData = (): PremiumReportData => ({
    userName: profile?.full_name || 'Usuário',
    period: {
      start: format(filters.startDate, "dd/MM/yyyy", { locale: ptBR }),
      end: format(filters.endDate, "dd/MM/yyyy", { locale: ptBR }),
    },
    financial: financial || null,
    projects: projects || null,
    clients: clients || null,
    timeReport: timeReport || null,
    previousPeriod,
  });

  const handleExportHTML = async () => {
    if (!canExport) {
      setShowUpgradeDialog(true);
      return;
    }

    setIsExportingHTML(true);
    try {
      const reportData = buildReportData();
      const htmlContent = generatePremiumHTML(reportData);

      // Create and download HTML file
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-freeladash-${format(new Date(), 'yyyy-MM-dd')}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Relatório HTML exportado com sucesso!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erro ao exportar relatório');
    } finally {
      setIsExportingHTML(false);
    }
  };

  const handleExportPDF = async () => {
    if (!canExport) {
      setShowUpgradeDialog(true);
      return;
    }

    setIsExportingPDF(true);
    try {
      const reportData = buildReportData();
      const htmlContent = generatePremiumHTML(reportData);

      // Call edge function to generate PDF
      const { data, error } = await supabase.functions.invoke('generate-pdf', {
        body: {
          html: htmlContent,
          filename: `relatorio-freeladash-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
        },
      });

      if (error) throw error;

      // The response should be a PDF blob
      if (data instanceof Blob) {
        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio-freeladash-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('PDF exportado com sucesso!');
      } else {
        throw new Error('Invalid response from PDF generator');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Erro ao gerar PDF. Tente o relatório HTML.');
    } finally {
      setIsExportingPDF(false);
    }
  };

  if (!canExport) {
    return (
      <>
        <div className="flex gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                className="gap-1.5 h-8 text-xs sm:text-sm opacity-60" 
                size="sm"
                onClick={() => setShowUpgradeDialog(true)}
              >
                <Lock className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Relatório</span> HTML
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Exportação disponível apenas no plano Pro</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                className="gap-1.5 h-8 text-xs sm:text-sm opacity-60" 
                size="sm"
                onClick={() => setShowUpgradeDialog(true)}
              >
                <Lock className="h-3.5 w-3.5" />
                PDF
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Exportação disponível apenas no plano Pro</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <AlertDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Recurso Exclusivo Pro</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    A <strong>exportação de relatórios</strong> está disponível apenas no plano Pro.
                  </p>
                  <p className="text-sm">
                    Faça upgrade para exportar relatórios premium em HTML e PDF, perfeitos para compartilhar com clientes.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={() => navigate("/settings")}>
                Fazer Upgrade
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <div className="flex gap-2">
      <Button 
        variant="outline" 
        className="gap-1.5 h-8 text-xs sm:text-sm" 
        size="sm"
        onClick={handleExportHTML}
        disabled={isExportingHTML || isExportingPDF}
      >
        {isExportingHTML ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <FileText className="h-3.5 w-3.5" />
        )}
        <span className="hidden sm:inline">Relatório</span> HTML
      </Button>
      <Button 
        variant="default"
        className="gap-1.5 h-8 text-xs sm:text-sm" 
        size="sm"
        onClick={handleExportPDF}
        disabled={isExportingHTML || isExportingPDF}
      >
        {isExportingPDF ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <FileDown className="h-3.5 w-3.5" />
        )}
        PDF
      </Button>
    </div>
  );
}
